import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Form,
  InputGroup,
  Badge,
  Button,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faShieldAlt,
  faCheckCircle,
  faCircle,
  faLayerGroup,
  faKey,
  faTimes,
  faAngleRight,
  faAngleDown,
  faHome,
  faBuildingUser,
  faFileText,
  faMinusCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import "./PermissoesManager.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useFormContext } from "react-hook-form";

interface PermissaoType {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
}

interface PerfilTreeNode {
  id: number;
  descricao: string;
  subMenus: PerfilTreeNode[];
  permissoes: PermissaoType[];
}

interface PermissoesManagerProps {
  disabled?: boolean;
}

// Map of module names to icons based on sitemap.tsx
const moduleNameIcons: Record<string, any> = {
  Inicial: { icon: faHome, iconSet: "font-awesome" },
  Cadastros: { icon: faFileText, iconSet: "font-awesome" },
  Operacional: { icon: faBuildingUser, iconSet: "font-awesome" },
};

// Updated getIconForPerfil function to handle different icon types from sitemap
const getIconForPerfil = (perfil: PerfilTreeNode) => {
  const name = perfil.descricao.toLowerCase();

  // Check for exact matches in our icon map
  for (const [key, iconData] of Object.entries(moduleNameIcons)) {
    if (name.includes(key.toLowerCase())) {
      // If it's a font-awesome icon, return it directly
      if (iconData.iconSet === "font-awesome") {
        return iconData.icon;
      }

      // For feather icons, map to equivalent Font Awesome icons
      if (iconData.iconSet === "feather") {
        if (iconData.icon === "file-text") return faKey;
        if (iconData.icon === "home") return faHome;
      }

      return iconData.icon;
    }
  }

  // Default icons based on content
  if (perfil.permissoes.length > 0) {
    return faKey;
  }

  return faLayerGroup;
};

const PermissoesManager: React.FC<PermissoesManagerProps> = ({
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeRootNode, setActiveRootNode] = useState<PerfilTreeNode | null>(
    null
  );
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [localSelected, setLocalSelected] = useState<number[]>([]);
  const [searchMatchNodes, setSearchMatchNodes] = useState<Set<number>>(
    new Set()
  );
  const [directMatchNodes, setDirectMatchNodes] = useState<Set<number>>(
    new Set()
  );
  const matchRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userExpandedNodesRef = useRef<Set<number>>(new Set());
  const isSearchActiveRef = useRef(false);

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const { isLoading: isLoadingPermissoesTree, data: permissoesTreeData } =
    useQuery({
      queryKey: ["permissoes-tree"],
      queryFn: async () => {
        const response = await axios.get("/api/permissao/tree");
        return response.data;
      },
    });

  const permissoesTree = (permissoesTreeData as PerfilTreeNode[]) || [];
  
  useEffect(() => {
    setLocalSelected(watch("permissoes") || []);
  }, [watch("permissoes")]);

  // Auto-select first root node if none is selected yet
  useEffect(() => {
    if (permissoesTree && permissoesTree.length > 0 && !activeRootNode) {
      setActiveRootNode(permissoesTree[0]);
    }
  }, [permissoesTree, activeRootNode]);

  // Helper function to directly check if a node or its permissions match the search term
  const nodeOrPermissionMatchesSearch = useCallback(
    (node: PerfilTreeNode, term: string): boolean => {
      if (!term) return false;
      const lowercaseTerm = term.toLowerCase();

      // Check node name
      if (node.descricao.toLowerCase().includes(lowercaseTerm)) {
        return true;
      }

      // Check permissions directly at this level only (not child nodes)
      return node.permissoes.some(
        (p) =>
          p.nome.toLowerCase().includes(lowercaseTerm) ||
          p.descricao.toLowerCase().includes(lowercaseTerm) ||
          p.codigo.toLowerCase().includes(lowercaseTerm)
      );
    },
    []
  );

  // Function to check if a node matches the search term
  const nodeMatchesSearch = useCallback(
    (node: PerfilTreeNode, term: string): boolean => {
      const lowercaseTerm = term.toLowerCase();

      // Check node name
      if (node.descricao.toLowerCase().includes(lowercaseTerm)) {
        return true;
      }

      // Check permissions
      if (
        node.permissoes.some(
          (p) =>
            p.nome.toLowerCase().includes(lowercaseTerm) ||
            p.descricao.toLowerCase().includes(lowercaseTerm) ||
            p.codigo.toLowerCase().includes(lowercaseTerm)
        )
      ) {
        return true;
      }

      // Check children recursively
      return node.subMenus.some((child) => nodeMatchesSearch(child, term));
    },
    []
  );

  // Updated function to track direct matches vs parent-only matches
  const findMatchingNodeIds = useCallback(
    (
      node: PerfilTreeNode,
      term: string,
      matches: Set<number>,
      directMatches: Set<number>
    ) => {
      if (!term) return { matches, directMatches };

      const lowercaseTerm = term.toLowerCase();

      // Check if current node's description matches
      const nodeDescriptionMatches = node.descricao
        .toLowerCase()
        .includes(lowercaseTerm);

      // Check if any of this node's direct permissions match
      const permissionsMatch = node.permissoes.some(
        (p) =>
          p.nome.toLowerCase().includes(lowercaseTerm) ||
          p.descricao.toLowerCase().includes(lowercaseTerm) ||
          p.codigo.toLowerCase().includes(lowercaseTerm)
      );

      // If this node directly matches by description or permissions, add it to both sets
      if (nodeDescriptionMatches || permissionsMatch) {
        matches.add(node.id);
        directMatches.add(node.id);
      }

      // Recursively check children
      let hasMatchingChild = false;
      for (const child of node.subMenus) {
        const result = findMatchingNodeIds(child, term, matches, directMatches);

        // If any child matches, mark this as a parent with matching children
        if (result.matches.has(child.id)) {
          hasMatchingChild = true;
        }
      }

      // If any child matches but this node doesn't directly match,
      // add this node only to the general matches set (for expansion)
      if (hasMatchingChild && !directMatches.has(node.id)) {
        matches.add(node.id);
      }

      return { matches, directMatches };
    },
    []
  );

  // Scroll to the first matching permission or node
  const scrollToFirstMatch = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (matchRefs.current.size > 0) {
        const matchElements = Array.from(matchRefs.current.values());
        if (matchElements.length > 0) {
          const firstMatchInActiveRoot = matchElements.find((el) => {
            if (!scrollableContentRef.current || !activeRootNode) return false;
            return scrollableContentRef.current.contains(el);
          });

          if (firstMatchInActiveRoot) {
            firstMatchInActiveRoot.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }
    }, 300);
  }, [activeRootNode]);

  // Update expanded nodes when search term changes
  useEffect(() => {
    if (!searchTerm || !permissoesTree) {
      setSearchMatchNodes(new Set());
      setDirectMatchNodes(new Set());
      isSearchActiveRef.current = false;
      return;
    }

    const matches = new Set<number>();
    const directMatches = new Set<number>();

    permissoesTree.forEach((rootNode) => {
      const result = findMatchingNodeIds(
        rootNode,
        searchTerm,
        matches,
        directMatches
      );
    });

    setSearchMatchNodes(matches);
    setDirectMatchNodes(directMatches);

    if (searchTerm.length > 2) {
      isSearchActiveRef.current = true;

      // Auto-expand nodes containing matches
      setExpandedNodes((prev) => {
        // In search mode, we keep previously expanded nodes
        return new Set([...prev, ...matches]);
      });

      if (activeRootNode && !nodeMatchesSearch(activeRootNode, searchTerm)) {
        const matchingRootNode = permissoesTree.find((node) =>
          nodeMatchesSearch(node, searchTerm)
        );
        if (matchingRootNode) {
          setActiveRootNode(matchingRootNode);
        }
      }

      matchRefs.current.clear();
      setTimeout(scrollToFirstMatch, 100);
    } else {
      isSearchActiveRef.current = false;
    }
  }, [
    searchTerm,
    permissoesTree,
    findMatchingNodeIds,
    nodeMatchesSearch,
    activeRootNode,
    scrollToFirstMatch,
  ]);

  useEffect(() => {
    matchRefs.current.clear();
    if (searchTerm.length > 2 && searchMatchNodes.size > 0) {
      setTimeout(scrollToFirstMatch, 100);
    }
  }, [activeRootNode, searchTerm, searchMatchNodes.size, scrollToFirstMatch]);

  const highlightMatch = (text: string): React.ReactNode => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Toggle node expansion in tree view with accordion behavior
  const toggleNodeExpansion = (nodeId: number) => {
    if (disabled) return;

    setExpandedNodes((prev) => {
      const newSet = new Set<number>();

      // If we're in search mode or this node is currently expanded, just toggle it
      if (isSearchActiveRef.current || prev.has(nodeId)) {
        // If currently expanded, remove it
        if (prev.has(nodeId)) {
          prev.forEach((id) => {
            if (id !== nodeId) newSet.add(id);
          });
          userExpandedNodesRef.current.delete(nodeId);
        } else {
          // Expand this node, keep others in search mode
          prev.forEach((id) => newSet.add(id));
          newSet.add(nodeId);
          userExpandedNodesRef.current.add(nodeId);
        }
      } else {
        // Normal accordion mode - expand only this node
        newSet.add(nodeId);
        userExpandedNodesRef.current = new Set([nodeId]);
      }

      return newSet;
    });
  };

  const clearSearch = () => {
    if (disabled) return;

    setSearchTerm("");
    setSearchMatchNodes(new Set());
    setDirectMatchNodes(new Set());
    matchRefs.current.clear();
    isSearchActiveRef.current = false;

    // Reset to accordion behavior - only keep the last user expanded node if any
    if (userExpandedNodesRef.current.size > 0) {
      // Get the last expanded node
      const lastExpanded = Array.from(userExpandedNodesRef.current).pop();
      setExpandedNodes(new Set(lastExpanded ? [lastExpanded] : []));
      userExpandedNodesRef.current = new Set(
        lastExpanded ? [lastExpanded] : []
      );
    } else {
      setExpandedNodes(new Set());
    }
  };

  const getAllPermissionIds = (node: PerfilTreeNode): number[] => {
    const permissionIds = node.permissoes.map((p) => p.id);

    for (const childNode of node.subMenus) {
      permissionIds.push(...getAllPermissionIds(childNode));
    }

    return permissionIds;
  };

  const togglePermission = (id: number) => {
    if (disabled) return;

    const newSelected = localSelected.includes(id)
      ? localSelected.filter((permId) => permId !== id)
      : [...localSelected, id];

    setLocalSelected(newSelected);
    setValue("permissoes", newSelected, { shouldDirty: true });
    trigger("permissoes");
  };

  const toggleNodePermissions = (node: PerfilTreeNode) => {
    if (disabled) return;

    const allNodePermissionIds = getAllPermissionIds(node);

    if (allNodePermissionIds.length === 0) return;

    const allSelected = allNodePermissionIds.every((id) =>
      localSelected.includes(id)
    );

    let newSelected: number[];

    if (allSelected) {
      newSelected = localSelected.filter(
        (id) => !allNodePermissionIds.includes(id)
      );
    } else {
      const toAdd = allNodePermissionIds.filter(
        (id) => !localSelected.includes(id)
      );
      newSelected = [...localSelected, ...toAdd];
    }

    setLocalSelected(newSelected);
    setValue("permissoes", newSelected, { shouldDirty: true });
    trigger("permissoes");
  };

  const toggleAllRootPermissions = () => {
    if (disabled || !activeRootNode) return;

    const allRootPermissionIds = getAllPermissionIds(activeRootNode);
    if (allRootPermissionIds.length === 0) return;

    const allSelected = allRootPermissionIds.every((id) =>
      localSelected.includes(id)
    );

    let newSelected: number[];

    if (allSelected) {
      newSelected = localSelected.filter(
        (id) => !allRootPermissionIds.includes(id)
      );
    } else {
      const toAdd = allRootPermissionIds.filter(
        (id) => !localSelected.includes(id)
      );
      newSelected = [...localSelected, ...toAdd];
    }

    setLocalSelected(newSelected);
    setValue("permissoes", newSelected, { shouldDirty: true });
    trigger("permissoes");
  };

  const getActiveRootSelectionState = (): "full" | "partial" | "none" => {
    if (!activeRootNode) return "none";
    return getNodeSelectionState(activeRootNode);
  };

  const getNodeSelectionState = (
    node: PerfilTreeNode
  ): "full" | "partial" | "none" => {
    const allPermissionIds = getAllPermissionIds(node);

    if (allPermissionIds.length === 0) return "none";

    const selectedCount = allPermissionIds.filter((id) =>
      localSelected.includes(id)
    ).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === allPermissionIds.length) return "full";
    return "partial";
  };

  const getNodePermissionCount = (node: PerfilTreeNode): number => {
    return getAllPermissionIds(node).length;
  };

  const getNodeSelectedCount = (node: PerfilTreeNode): number => {
    const allPermissionIds = getAllPermissionIds(node);
    return allPermissionIds.filter((id) => localSelected.includes(id)).length;
  };

  const renderPermissionItem = (permission: PermissaoType) => {
    const isSelected = localSelected.includes(permission.id);
    const matchesSearch =
      searchTerm &&
      (permission.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.codigo.toLowerCase().includes(searchTerm.toLowerCase()));

    const refKey = `permission-${permission.id}`;

    return (
      <div
        className={`permission-item bg-body ${isSelected ? "selected" : ""} ${
          matchesSearch ? "search-match" : ""
        }`}
        onClick={() => togglePermission(permission.id)}
        ref={(node) => {
          if (matchesSearch && node) {
            matchRefs.current.set(refKey, node);
          } else if (!matchesSearch) {
            matchRefs.current.delete(refKey);
          }
        }}
      >
        <div className="selection-indicator">
          {isSelected ? (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
          ) : (
            <FontAwesomeIcon icon={faCircle} className="text-muted" />
          )}
        </div>

        <div className="permission-details">
          <div className="permission-name">
            {highlightMatch(permission.nome)}
          </div>
          <div className="d-flex align-items-center">
            <div className="permission-code">
              {highlightMatch(permission.descricao)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNode = (node: PerfilTreeNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const selectionState = getNodeSelectionState(node);
    const hasChildren = node.subMenus.length > 0 || node.permissoes.length > 0;
    const matchesSearch = searchMatchNodes.has(node.id);
    const isDirectMatch = directMatchNodes.has(node.id);

    const refKey = `node-${node.id}`;

    return (
      <div
        key={node.id}
        className="node"
        style={{ marginLeft: `${level * 16}px` }}
      >
        <div
          className={`node-header bg-body ${
            hasChildren ? "has-children" : ""
          } ${isExpanded ? "expanded" : ""} ${
            isDirectMatch
              ? "search-match"
              : matchesSearch
              ? "search-match-parent"
              : ""
          }`}
          onClick={() => hasChildren && toggleNodeExpansion(node.id)}
          ref={(el) => {
            const directMatch =
              node.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
              node.permissoes.some(
                (p) =>
                  p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  p.descricao
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  p.codigo.toLowerCase().includes(searchTerm.toLowerCase())
              );

            if (directMatch && el) {
              matchRefs.current.set(refKey, el);
            } else if (!directMatch) {
              matchRefs.current.delete(refKey);
            }
          }}
        >
          <div className="d-flex align-items-center">
            {hasChildren && (
              <FontAwesomeIcon
                icon={isExpanded ? faAngleDown : faAngleRight}
                className="toggle-icon me-2"
              />
            )}

            <span className="node-title">{highlightMatch(node.descricao)}</span>

            <Badge
              bg={
                selectionState === "full"
                  ? "success"
                  : selectionState === "partial"
                  ? "warning"
                  : "secondary"
              }
              className="ms-2"
            >
              {getNodeSelectedCount(node)}/{getNodePermissionCount(node)}
            </Badge>
          </div>

          <div
            className="selection-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleNodePermissions(node);
            }}
          >
            {selectionState === "full" && (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
            )}
            {selectionState === "partial" && (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id={`tooltip-partial-${node.id}`}>
                    <strong>Seleção parcial:</strong> Algumas permissões deste
                    grupo estão selecionadas, outras não. Clique para selecionar
                    todas as permissões.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon
                  icon={faMinusCircle}
                  className="text-warning"
                />
              </OverlayTrigger>
            )}
            {selectionState === "none" && (
              <FontAwesomeIcon icon={faCircle} className="text-muted" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="node-content">
            {node.permissoes.length > 0 && (
              <div className="permissions-list">
                {node.permissoes.map(renderPermissionItem)}
              </div>
            )}

            {node.subMenus.map((childNode) => renderNode(childNode, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <FontAwesomeIcon icon={faShieldAlt} className="empty-icon mb-3" />
      <h5>Selecione um módulo</h5>
      <p className="text-muted">
        Clique em um módulo no menu esquerdo para visualizar e gerenciar suas
        permissões
      </p>
    </div>
  );

  return (
    <Card.Body>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center align-items-start mb-3">
        <h5 className="mb-2 mb-md-0">
          <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
          Permissões
        </h5>
        <span>{localSelected.length} permissões selecionadas</span>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <FontAwesomeIcon icon={faSearch} />
        </InputGroup.Text>
        <Form.Control
          placeholder="Pesquisar permissões..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
        />
        {searchTerm && (
          <Button
            variant="outline-secondary"
            onClick={clearSearch}
            title="Limpar pesquisa"
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </InputGroup>

      {permissoesTree && permissoesTree.length > 0 ? (
        <div className="tree-container">
          <div className="root-nodes-sidebar bg-body">
            {permissoesTree.map((rootNode) => {
              // Check if this root node directly matches via its own description or its permissions
              const rootNodeDirectlyMatches =
                searchTerm &&
                nodeOrPermissionMatchesSearch(rootNode, searchTerm);

              return (
                <div
                  key={rootNode.id}
                  className={`root-node-item ${
                    activeRootNode?.id === rootNode.id ? "active" : ""
                  } ${
                    directMatchNodes.has(rootNode.id)
                      ? "search-match"
                      : searchMatchNodes.has(rootNode.id)
                      ? "search-match-parent"
                      : ""
                  }`}
                  onClick={() => !disabled && setActiveRootNode(rootNode)}
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={getIconForPerfil(rootNode)}
                      className="me-2 module-icon"
                    />
                    <div className="flex-grow-1">
                      <div className="root-node-name">
                        {highlightMatch(rootNode.descricao)}
                      </div>
                      <div className="selection-count">
                        {getNodeSelectedCount(rootNode)}/
                        {getNodePermissionCount(rootNode)}
                      </div>
                    </div>
                    {getNodeSelectionState(rootNode) === "full" && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-success"
                      />
                    )}
                    {getNodeSelectionState(rootNode) === "partial" && (
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <Tooltip id={`tooltip-root-partial-${rootNode.id}`}>
                            <strong>Seleção parcial:</strong> Algumas permissões
                            deste módulo estão selecionadas, outras não.
                            Selecione o módulo para gerenciar suas permissões.
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon
                          icon={faMinusCircle}
                          className="text-warning"
                        />
                      </OverlayTrigger>
                    )}
                    {getNodeSelectionState(rootNode) === "none" && (
                      <FontAwesomeIcon icon={faCircle} className="text-muted" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="nodes-content">
            {activeRootNode ? (
              <div className="node-detail-container">
                <div className="module-header-sticky">
                  <h5 className="mb-1">
                    <FontAwesomeIcon
                      icon={getIconForPerfil(activeRootNode)}
                      className="me-2"
                    />
                    {highlightMatch(activeRootNode.descricao)}
                  </h5>

                  <div className="module-actions-header">
                    <div className="module-section-title">
                      <FontAwesomeIcon
                        icon={faLayerGroup}
                        className="module-icon"
                      />
                      <span>Módulos e Permissões</span>

                      {getActiveRootSelectionState() === "partial" && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-module-info">
                              <strong>Seleção parcial:</strong> Este módulo tem
                              algumas permissões selecionadas. Use o interruptor
                              para selecionar todas ou ver abaixo quais estão
                              selecionadas.
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className="ms-2 text-warning"
                            style={{ fontSize: "0.9rem", cursor: "help" }}
                          />
                        </OverlayTrigger>
                      )}
                    </div>

                    <Form.Check
                      type="switch"
                      id="toggle-all-permissions"
                      label="Selecionar todas"
                      checked={getActiveRootSelectionState() === "full"}
                      onChange={toggleAllRootPermissions}
                      className="permission-toggle-switch"
                      disabled={disabled}
                    />
                  </div>

                  {activeRootNode.permissoes.length > 0 && (
                    <div className="root-permissions bg-body">
                      <div className="permissions-header">
                        <FontAwesomeIcon icon={faKey} className="module-icon" />
                        <span>Permissões diretas</span>
                      </div>
                      <div className="permissions-list">
                        {activeRootNode.permissoes.map(renderPermissionItem)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="scrollable-content" ref={scrollableContentRef}>
                  {activeRootNode.subMenus.length > 0 && (
                    <div className="sub-nodes">
                      {activeRootNode.subMenus.map((node) => renderNode(node))}
                    </div>
                  )}

                  {activeRootNode.subMenus.length === 0 &&
                    activeRootNode.permissoes.length === 0 && (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          Este módulo não possui permissões ou funcionalidades.
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              renderEmptyState()
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-5">
          <div className="text-muted">
            {searchTerm
              ? "Nenhuma permissão encontrada com os critérios de busca."
              : "Não há permissões disponíveis."}
          </div>
        </div>
      )}
    </Card.Body>
  );
};

export default PermissoesManager;
